/**
 * Theme Provider Component
 * Provides theme context and styled-components theme provider
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { lightTheme, darkTheme, ColorTheme } from '../tokens/colors';
import {
  fontFamilies,
  fontWeights,
  fontSizes,
  lineHeights,
  letterSpacing,
  typography,
} from '../tokens/typography';
import {
  spacing,
  semanticSpacing,
  borderRadius,
  shadows,
  zIndex,
} from '../tokens/spacing';
import {
  breakpointValues,
  mediaQueries,
  containerMaxWidths,
  gridColumns,
  responsive,
  touchTargets,
} from '../tokens/breakpoints';

// Theme mode type
export type ThemeMode = 'light' | 'dark' | 'system';

// Complete theme interface
export interface Theme {
  mode: ThemeMode;
  colors: ColorTheme;
  typography: typeof typography;
  spacing: typeof spacing;
  semanticSpacing: typeof semanticSpacing;
  borderRadius: typeof borderRadius;
  shadows: typeof shadows;
  zIndex: typeof zIndex;
  breakpoints: typeof breakpointValues;
  mediaQueries: typeof mediaQueries;
  containerMaxWidths: typeof containerMaxWidths;
  gridColumns: typeof gridColumns;
  responsive: typeof responsive;
  touchTargets: typeof touchTargets;
  fontFamilies: typeof fontFamilies;
  fontWeights: typeof fontWeights;
  fontSizes: typeof fontSizes;
  lineHeights: typeof lineHeights;
  letterSpacing: typeof letterSpacing;
}

// Theme context interface
interface ThemeContextValue {
  theme: Theme;
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
  isDark: boolean;
  isLight: boolean;
  isSystem: boolean;
}

// Create theme context
const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// Theme provider props
interface ThemeProviderProps {
  children: ReactNode;
  defaultMode?: ThemeMode;
  storageKey?: string;
}

// Local storage key for theme preference
const DEFAULT_STORAGE_KEY = 'theme-mode';

// Create complete theme object
const createTheme = (mode: ThemeMode): Theme => {
  const resolvedMode = mode === 'system' ? getSystemTheme() : mode;
  const colors = resolvedMode === 'dark' ? darkTheme : lightTheme;

  return {
    mode,
    colors,
    typography,
    spacing,
    semanticSpacing,
    borderRadius,
    shadows,
    zIndex,
    breakpoints: breakpointValues,
    mediaQueries,
    containerMaxWidths,
    gridColumns,
    responsive,
    touchTargets,
    fontFamilies,
    fontWeights,
    fontSizes,
    lineHeights,
    letterSpacing,
  };
};

// Get system theme preference
const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light';
  
  // Handle case where matchMedia might not be available (e.g., in tests)
  if (!window.matchMedia) return 'light';
  
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  return mediaQuery && mediaQuery.matches ? 'dark' : 'light';
};

// Get stored theme preference
const getStoredTheme = (storageKey: string): ThemeMode => {
  if (typeof window === 'undefined') return 'system';
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      return stored as ThemeMode;
    }
  } catch (error) {
    console.warn('Failed to read theme from localStorage:', error);
  }
  return 'system';
};

// Store theme preference
const storeTheme = (mode: ThemeMode, storageKey: string): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(storageKey, mode);
  } catch (error) {
    console.warn('Failed to store theme in localStorage:', error);
  }
};

// Theme provider component
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultMode = 'system',
  storageKey = DEFAULT_STORAGE_KEY,
}) => {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    return getStoredTheme(storageKey) || defaultMode;
  });

  const theme = createTheme(mode);

  // Set mode and persist to storage
  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode);
    storeTheme(newMode, storageKey);
  };

  // Toggle between light and dark (skip system)
  const toggleMode = () => {
    const currentResolvedMode = mode === 'system' ? getSystemTheme() : mode;
    setMode(currentResolvedMode === 'light' ? 'dark' : 'light');
  };

  // Listen for system theme changes
  useEffect(() => {
    if (mode !== 'system') return;
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    if (!mediaQuery) return;

    const handleChange = () => {
      // Force re-render when system theme changes
      setModeState('system');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [mode]);

  // Update document class for CSS custom properties
  useEffect(() => {
    const resolvedMode = mode === 'system' ? getSystemTheme() : mode;
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(resolvedMode);
    document.documentElement.setAttribute('data-theme', resolvedMode);
  }, [mode]);

  const contextValue: ThemeContextValue = {
    theme,
    mode,
    setMode,
    toggleMode,
    isDark: (mode === 'system' ? getSystemTheme() : mode) === 'dark',
    isLight: (mode === 'system' ? getSystemTheme() : mode) === 'light',
    isSystem: mode === 'system',
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <StyledThemeProvider theme={theme}>
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

// Hook to use theme context
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Hook to get theme object directly (for styled-components)
export const useStyledTheme = (): Theme => {
  const { theme } = useTheme();
  return theme;
};

// Export theme type for styled-components
export type { Theme as StyledTheme };